import React from 'react';
import { AlertTriangle, RefreshCw, ChevronRight } from 'lucide-react';

// checkIn/checkOut live inside offer.shifts[0], NOT at the top level.
function resolveStepLabel(offer) {
  if (!offer) return 'Offer Sent';
  if (offer.status === 'paid')                        return 'Paid';
  if (offer.status === 'completed')                   return 'Completed';
  if (offer.shifts?.[0]?.checkOut?.confirmed)         return 'Checked Out';
  if (offer.shifts?.[0]?.checkIn?.confirmed)          return 'Checked In';
  if (offer.status === 'accepted' || offer.status === 'active') return 'Accepted';
  return 'Offer Sent';
}

const OFFER_STATUS = {
  open:      { label: 'Offered',   colors: 'bg-amber-50 text-amber-700 border-amber-200'       },
  accepted:  { label: 'Assigned',  colors: 'bg-sky-50 text-sky-700 border-sky-200'             },
  active:    { label: 'Active',    colors: 'bg-blue-50 text-blue-700 border-blue-200'           },
  completed: { label: 'Completed', colors: 'bg-gray-50 text-gray-600 border-gray-200'           },
  disputed:  { label: 'Disputed',  colors: 'bg-red-50 text-red-600 border-red-200'              },
  resolved:  { label: 'Resolved',  colors: 'bg-emerald-50 text-emerald-700 border-emerald-200'  },
  paid:      { label: 'Paid',      colors: 'bg-emerald-50 text-emerald-700 border-emerald-200'  },
  expired:   { label: 'Expired',   colors: 'bg-gray-50 text-gray-400 border-gray-200'           },
  rejected:  { label: 'Declined',  colors: 'bg-gray-50 text-gray-400 border-gray-200'           },
};

function fmtTime(d) {
  if (!d) return null;
  return new Date(d).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
}

/**
 * OfficerRow — one clickable row per offer in OfficerRoster.
 *
 * Clicking the row opens OfferPanel on the right (parent handles navigation).
 * No accordion — all detail lives in OfferPanel.
 *
 * Props:
 *   offer      — full offer object
 *   isSelected — bool (highlights the row when OfferPanel is open for this offer)
 *   onSelect   — (offer) => void
 */
const OfficerRow = ({ offer, isSelected, onSelect }) => {
  const resource     = offer.provider_resource_id;
  const user         = resource?.user_id || {};
  const name         = user.firstName
    ? `${user.firstName} ${user.lastName || ''}`.trim()
    : 'Officer';

  const shiftEmbed   = offer.shifts?.[0];
  const checkedIn    = shiftEmbed?.checkIn?.confirmed;
  const checkedOut   = shiftEmbed?.checkOut?.confirmed;
  const checkInTime  = shiftEmbed?.checkIn?.timestamp;
  const checkOutTime = shiftEmbed?.checkOut?.timestamp;

  const stepLabel    = resolveStepLabel(offer);
  const statusCfg    = OFFER_STATUS[offer.status] ?? OFFER_STATUS.open;
  const isDisputed   = offer.status === 'disputed';
  const isHistorical = ['expired', 'rejected'].includes(offer.status);
  const rate         = offer.negotiations?.providerHourlyRate;

  return (
    <div
      className={`border-b border-border last:border-b-0 ${
        isHistorical ? 'opacity-60' : ''
      }`}
    >
      <div
        className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
          isSelected
            ? 'bg-brand-blue-dark/8 border-l-2 border-brand-blue-dark'
            : 'hover:bg-muted/20'
        }`}
        onClick={() => onSelect(offer)}
      >
        {/* Avatar */}
        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 overflow-hidden">
          {user.profilePictureUrl ? (
            <img src={user.profilePictureUrl} alt={name} className="h-full w-full object-cover" />
          ) : (
            <span className="text-[10px] font-bold text-primary">{name.charAt(0).toUpperCase()}</span>
          )}
        </div>

        {/* Name + step label */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <p className="text-xs font-semibold text-card-foreground truncate">{name}</p>
            {offer.parentOfferId && (
              <span className="inline-flex items-center gap-0.5 rounded border border-purple-200 bg-purple-50 px-1.5 py-0.5 text-[9px] font-medium text-purple-700">
                <RefreshCw size={8} /> Revised
              </span>
            )}
            {isDisputed && (
              <AlertTriangle size={11} className="text-amber-500 flex-shrink-0" />
            )}
          </div>
          <p className="text-[10px] text-muted-foreground">{stepLabel}</p>
        </div>

        {/* Check-in / check-out quick timestamps */}
        {(checkedIn || checkedOut) && (
          <div className="hidden sm:flex flex-col items-end gap-0.5 flex-shrink-0">
            {checkedIn  && <span className="text-[10px] text-emerald-600 font-medium">In {fmtTime(checkInTime)}</span>}
            {checkedOut && <span className="text-[10px] text-brand-blue-dark font-medium">Out {fmtTime(checkOutTime)}</span>}
          </div>
        )}

        {/* Rate */}
        {rate != null && (
          <span className="text-[10px] text-muted-foreground flex-shrink-0">${rate.toFixed(2)}/hr</span>
        )}

        {/* Status badge */}
        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium flex-shrink-0 ${statusCfg.colors}`}>
          {statusCfg.label}
        </span>

        {/* Chevron affordance */}
        <ChevronRight size={13} className={`flex-shrink-0 transition-colors ${
          isSelected ? 'text-brand-blue-dark' : 'text-muted-foreground/40'
        }`} />
      </div>
    </div>
  );
};

export default OfficerRow;
